import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { Spacing, Dimensions } from '@/shared/theme';
import { Text } from '@/shared/ui';
import { ResultTitle, ResultBody } from '@/shared/resultText';
import { StartOverModal } from '@/components/StartOverModal';
import { useAppFlow, STEP_KEYS } from '@/hooks/useAppFlow';
import emailSentImage from '@/assets/images/email-sent.svg';
import { VIEW_EMAIL_SAVED } from '@/consts/events';
import { useAnalytics } from '@/hooks';

// A POST_REGISTRATION_PATHS route (Layout.tsx): a plain, unframed, normal
// page-scroll screen with no rail/phase-bar to anchor to. min-height +
// justify-content:center keep this vertically centered (Boris `.rg-result`)
// instead of clumping at the top of an otherwise-empty cream page, matching
// EligibilityResultStep's already-fixed terminal-screen pattern.
const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: calc(100vh - ${Dimensions.headerHeight}px);
  gap: ${Spacing.x2}px;
  padding: ${Spacing.x3}px ${Spacing.x2}px;

  @media (min-width: 768px) {
    padding: ${Spacing.x5}px ${Spacing.x2}px;
    gap: ${Spacing.x3}px;
  }
`;

const IllustrationWrapper = styled.div`
  width: 100%;
  max-width: 144px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${Spacing.x1}px;

  @media (min-width: 768px) {
    max-width: 180px;
    margin-bottom: ${Spacing.x2}px;
  }
`;

const IllustrationImage = styled.img`
  width: 100%;
  max-width: 144px;
  height: auto;

  @media (min-width: 768px) {
    max-width: 180px;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${Spacing.x1_5}px;
  width: 100%;
  max-width: 343px;
  text-align: center;

  @media (min-width: 768px) {
    max-width: 600px;
    gap: ${Spacing.x2}px;
  }
`;

const GetInTouchLink = styled.a`
  color: #04747a;
  text-decoration: underline;
  text-decoration-skip-ink: none;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`;

const EmailSentStep: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [showStartOverModal, setShowStartOverModal] = useState(false);
  const [email, setEmail] = useState('');
  const { trackView } = useAnalytics();

  // Integrate useAppFlow with back navigation blocking
  const { resetBlocker } = useAppFlow(STEP_KEYS.EMAIL_SENT, {
    onBackNavigationBlocked: () => {
      setShowStartOverModal(true);
    },
  });

  useEffect(() => {
    trackView({ event: VIEW_EMAIL_SAVED });
  }, [trackView]);

  useEffect(() => {
    // Get email from navigation state
    const state = location.state as { email?: string } | null;

    const userEmail = state?.email;
    if (userEmail) {
      setEmail(userEmail);
    }
  }, [location.state]);

  const handleStartOver = () => {
    setShowStartOverModal(false);
    navigate('/');
  };

  const handleGetInTouch = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/cap-reached');
  };

  return (
    <Container>
      <IllustrationWrapper>
        <IllustrationImage src={emailSentImage} alt="" aria-hidden="true" />
      </IllustrationWrapper>

      <ContentWrapper>
        <ResultTitle size="h2" centered>
          {t('email-sent.title')}
        </ResultTitle>

        <ResultBody size="p1" centered>
          {t('email-sent.message', { email })}
        </ResultBody>

        <Text size="p1" centered style={{ marginTop: 0 }}>
          <Text size="p1" bold centered>
            {t('email-sent.wrong-email')}
          </Text>{' '}
          <br />
          {t('email-sent.go-back')}{' '}
          <GetInTouchLink onClick={handleGetInTouch}>
            {t('email-sent.enter-different-email')}
          </GetInTouchLink>
        </Text>
      </ContentWrapper>

      {/* Start Over Modal */}
      <StartOverModal
        isOpen={showStartOverModal}
        onConfirm={handleStartOver}
        onCancel={() => {
          setShowStartOverModal(false);
          resetBlocker();
        }}
      />
    </Container>
  );
};

export { EmailSentStep };
